import { useState } from 'react'
import { Edit2, Save, X } from 'lucide-react'
import { SystemSetting } from '@/types'
import { useUpdateSetting } from '@hooks/useSettings'

interface SettingsListProps {
  settings: SystemSetting[]
  category?: string
}

export function SettingsList({ settings, category }: SettingsListProps) {
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const updateSetting = useUpdateSetting()

  const filteredSettings = category
    ? settings.filter(s => s.category === category)
    : settings

  const groupedSettings = filteredSettings.reduce((acc, setting) => {
    if (!acc[setting.category]) {
      acc[setting.category] = []
    }
    acc[setting.category].push(setting)
    return acc
  }, {} as Record<string, SystemSetting[]>)

  const handleEdit = (setting: SystemSetting) => {
    setEditingKey(setting.key)
    setEditValue(setting.value)
  }

  const handleSave = async (key: string) => {
    try {
      await updateSetting.mutateAsync({ key, value: editValue })
      setEditingKey(null)
      setEditValue('')
    } catch (error) {
      console.error('Failed to update setting:', error)
    }
  }

  const handleCancel = () => {
    setEditingKey(null)
    setEditValue('')
  }

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      tracking: 'bg-blue-100 text-blue-800',
      system: 'bg-purple-100 text-purple-800',
      email: 'bg-green-100 text-green-800',
      security: 'bg-red-100 text-red-800',
      ui: 'bg-yellow-100 text-yellow-800',
    }
    return colors[category] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6">
      {Object.entries(groupedSettings).map(([categoryName, categorySettings]) => (
        <div key={categoryName} className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <span className={`rounded-full px-3 py-1 text-sm font-medium ${getCategoryColor(categoryName)}`}>
                {categoryName}
              </span>
              <span className="text-sm text-gray-500">
                {categorySettings.length} {categorySettings.length === 1 ? 'setting' : 'settings'}
              </span>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {categorySettings.map((setting) => {
              const isEditing = editingKey === setting.key

              return (
                <div key={setting.key} className="px-6 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-medium text-gray-900">{setting.key}</h3>
                        {setting.isPublic && (
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                            Public
                          </span>
                        )}
                      </div>
                      {setting.description && (
                        <p className="text-sm text-gray-500 mb-2">{setting.description}</p>
                      )}

                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSave(setting.key)
                              } else if (e.key === 'Escape') {
                                handleCancel()
                              }
                            }}
                          />
                          <button
                            onClick={() => handleSave(setting.key)}
                            disabled={updateSetting.isPending}
                            className="rounded-lg bg-primary px-3 py-2 text-white hover:bg-primary/90 disabled:opacity-50"
                            title="Save"
                          >
                            <Save size={16} />
                          </button>
                          <button
                            onClick={handleCancel}
                            disabled={updateSetting.isPending}
                            className="rounded-lg bg-gray-200 px-3 py-2 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <code className="flex-1 rounded bg-gray-100 px-3 py-2 text-sm font-mono text-gray-800">
                            {setting.value}
                          </code>
                          <span className="text-xs text-gray-500">{setting.type}</span>
                        </div>
                      )}
                    </div>

                    {!isEditing && (
                      <button
                        onClick={() => handleEdit(setting)}
                        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        title="Edit setting"
                      >
                        <Edit2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {filteredSettings.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center">
          <p className="text-gray-500">No settings found</p>
        </div>
      )}
    </div>
  )
}
